import React, {memo, useEffect, useState} from 'react';
import {
  View,
  Image,
  TouchableOpacity,
  Clipboard,
  StyleSheet,
  LayoutChangeEvent,
} from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withSequence,
  withTiming,
  Easing,
  interpolate,
} from 'react-native-reanimated';
import {LinearGradient} from 'expo-linear-gradient';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import CCText from '@/Atoms/CCText';
import Box from '@/Atoms/Box';
import {styles} from './styles';
import {BANNER_DATA, BrightChampsBannerConfig} from './config';
import MaterialCommunityIcon from 'react-native-vector-icons/MaterialCommunityIcons';
import CCButton from '@/Atoms/CCButton';
import CCColors from '@/Utils/CCColors';
import { FontSizes } from '@/Utils/Dimensions';

interface BrightChampsBannerProps {
  config?: BrightChampsBannerConfig;
}

const BrightChampsBanner: React.FC<BrightChampsBannerProps> = ({
  config = BANNER_DATA,
}) => {
  const {isMobileOrTab} = useMediaQuery();
  const shimmerProgress = useSharedValue(0);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    shimmerProgress.value = withRepeat(
      withSequence(
        withTiming(1, {
          duration: 3500,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
        }),
        withTiming(0, {duration: 0}),
      ),
      -1,
      false,
    );
  }, [shimmerProgress]);

  const onLayout = (event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  };

  const shimmerStyle = useAnimatedStyle(() => {
    const translateX = interpolate(
      shimmerProgress.value,
      [0, 1],
      [-containerWidth || 500, containerWidth || 500],
    );
    return {
      transform: [{translateX}, {skewX: '-25deg'}],
    };
  });

  const handleCopyCoupon = () => {
    if (!config?.couponCode) return;
    Clipboard.setString(config.couponCode);
    config.onCopyCoupon?.();
  };

  const renderShimmer = () => (
    <Animated.View
      style={[
        StyleSheet.absoluteFillObject,
        shimmerStyle,
        {width: '40%', height: '100%', opacity: 0.15},
      ]}>
      <LinearGradient
        colors={[
          'transparent',
          'rgba(255, 255, 255, 0.02)',
          'rgba(255, 255, 255, 0.6)',
          'rgba(255, 255, 255, 0.02)',
          'transparent',
        ]}
        start={{x: 0, y: 0}}
        end={{x: 1, y: 0}}
        style={{flex: 1}}
      />
    </Animated.View>
  );

  const renderTags = () => {
    const activeTags = isMobileOrTab ? config?.tagsMobile : config?.tags;
    if (!activeTags?.length) return null;

    return (
      <View style={styles.tagsContainer}>
        {activeTags.map((tag, index) => (
          <React.Fragment key={index}>
            <CCText
              fontSize={isMobileOrTab ? FontSizes[12] : FontSizes[14]}
              color={CCColors?.White}
              style={styles.tag}>
              {tag}
            </CCText>
            {index < activeTags.length - 1 && (
              <CCText
                fontSize={FontSizes[14]}
                color={CCColors?.White}
                style={styles.tagSeparator}>
                •
              </CCText>
            )}
          </React.Fragment>
        ))}
      </View>
    );
  };

  const renderCoupon = () => {
    if (!config?.couponCode) return null;
    return (
      <TouchableOpacity
        style={styles.couponContainer}
        onPress={handleCopyCoupon}
        activeOpacity={0.8}>
        <Box style={styles.couponContainerInner}>
          <CCText style={styles.couponText}>{config.couponCode}</CCText>
          <MaterialCommunityIcon
            name="content-copy"
            size={16}
            color={CCColors?.TextColor?.Dark}
          />
        </Box>
      </TouchableOpacity>
    );
  };

  const renderOffer = () => (
    <View style={styles.offerContainer}>
      <CCText style={styles.upToText}>UP TO</CCText>
      <CCText style={styles.offText}>25% OFF</CCText>
    </View>
  );

  if (!config) return null;

  if (isMobileOrTab) {
    return (
      <Box style={{paddingHorizontal: 8, width: '100%', paddingTop: 20}}>
        <Box style={styles.mobileContainer} onLayout={onLayout}>
          {/* {renderShimmer()} */}
          <View style={styles.mobileLeftContent}>
            <View style={[styles.logoContainer, styles.mobileLogoContainer]}>
              {config.logo && (
                <Image source={config.logo} style={styles.logo} />
              )}
              {renderCoupon()}
            </View>

            {config.title && (
              <CCText style={[styles.title, styles.mobileTitle]}>
                {config.title}
              </CCText>
            )}
            {renderTags()}

            {config.buttonText && (
              <CCButton
                style={{width: 180, marginTop: 10}}
                onPress={() => config?.onPress?.()}>
                <CCText style={styles.btnText}>{config.buttonText}</CCText>
              </CCButton>
            )}
          </View>
          {config.mainImageMobile && (
            <Image
              source={config.mainImageMobile}
              style={styles.mobileGirlImage}
            />
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box style={styles.container} onLayout={onLayout}>
      {/* {renderShimmer()} */}
      <View style={styles.leftContent}>
        {config.title && <CCText fontSize={FontSizes[24]} style={styles.title}>{config.title}</CCText>}
        {renderTags()}

        <View style={styles.bottomRow}>
          {config.buttonText && (
            <CCButton onPress={() => config?.onPress?.()}>
              <CCText style={styles.btnText}>{config.buttonText}</CCText>
            </CCButton>
          )}

          {renderOffer()}
          {renderCoupon()}
        </View>
      </View>

      <View
        style={{
          position: 'absolute',
          top: 20,
          left: '50%',
          transform: [{translateX: 50}],
        }}>
        {config.logo && <Image source={config.logo} style={styles.logo} />}
      </View>

      {config.mainImage && (
        <Image source={config.mainImage} style={styles.girlImage} />
      )}
    </Box>
  );
};

export default memo(BrightChampsBanner);
